/**
 * ArticleWebPublishService — "Publish with payments (web)" for articles (v2.20.0).
 *
 * Publishes an article draft through the SAME /publish/web pipeline as
 * onboarding funnels: the article serves at the funnel ROOT path
 * ({domain}/{alias}) with SERVER_DATA, payments and the full placement
 * settings layer (redirect URL, variables, paymentConfig baking).
 *
 * The backing web placement is created programmatically with
 * `sourceKind: 'article'` — it is EXCLUDED from all placement lists and
 * pickers (WebPlacementService.list filters it), mirroring how article
 * roots are excluded from funnel lists. The pure-CDN layer (/a/{alias})
 * stays untouched as the second publish mode.
 */

import type { FlowDocument } from '@segmently/schema-models/v2';
import { WebPlacementService } from '@modules/firebase-core';
import type { WebPlacement, WebPlacementConfig } from '@modules/webplacements/types/webplacements.types';
import { BuilderApiClientImpl } from '@modules/placements/services/BuilderApiClientImpl';
import { ProjectAnalyticsService } from '@modules/analytics-settings/services/ProjectAnalyticsService';

export interface ArticleWebPublishInput {
  projectId: string;
  /** Article draft id — recorded as the placement's funnelId reference */
  articleId: string;
  alias: string;
  title: string;
  flowDocument: FlowDocument;
  /** User email for placement bookkeeping */
  createdBy: string;
}

export interface ArticleWebPublishResult {
  success: boolean;
  /** Root serving URL: {origin}/{alias} (SERVER_DATA + payments layer) */
  webUrl: string;
  placementId: string;
  error?: string;
}

/** Synthetic versionId — drafts carry the FlowDocument directly (no versions). */
const ARTICLE_DRAFT_VERSION_ID = 'article-draft';

export class ArticleWebPublishService {
  private readonly placements: WebPlacementService;
  private readonly analytics: ProjectAnalyticsService;
  private readonly builderClient: BuilderApiClientImpl;

  constructor(
    placements?: WebPlacementService,
    analytics?: ProjectAnalyticsService,
    builderClient?: BuilderApiClientImpl,
  ) {
    this.placements = placements ?? new WebPlacementService();
    this.analytics = analytics ?? new ProjectAnalyticsService();
    this.builderClient = builderClient ?? this.createDefaultBuilderClient();
  }

  private createDefaultBuilderClient(): BuilderApiClientImpl {
    const builderUrl = import.meta.env.VITE_BUILDER_API_URL;
    if (!builderUrl) {
      throw new Error('VITE_BUILDER_API_URL is not set.');
    }
    return new BuilderApiClientImpl(builderUrl);
  }

  /**
   * @param placementOverride — a placement the SETTINGS DIALOG already saved
   * (R6b): used as-is so every form setting (paymentConfig, redirect, data
   * collection, entry rules, SEO, webhooks) bakes into the publish. Without
   * it the service find-or-creates a bare tagged placement (legacy one-click).
   */
  async publish(
    input: ArticleWebPublishInput,
    placementOverride?: WebPlacement,
  ): Promise<ArticleWebPublishResult> {
    const { projectId, articleId, title, flowDocument, createdBy } = input;
    const alias = placementOverride?.alias ?? input.alias;

    // 1. Find-or-create the tagged placement (same alias as the CDN layer —
    //    the namespaces are disjoint: web-v2/* vs assets/articles/*).
    let placement = placementOverride ?? await this.placements.getByAlias(projectId, alias);
    if (placement && placement.sourceKind !== 'article') {
      return {
        success: false,
        webUrl: '',
        placementId: placement.id,
        error: `Alias "${alias}" is already used by a web placement. Change the article alias.`,
      };
    }
    if (!placement) {
      placement = await this.placements.create(projectId, {
        name: title || alias,
        alias,
        type: 'single',
        sourceKind: 'article',
        config: {
          funnelId: articleId,
          versionId: ARTICLE_DRAFT_VERSION_ID,
          entityName: title || alias,
        },
        createdBy,
      });
    }

    // 2. Publish through the standard web pipeline (SSG + SERVER_DATA +
    //    payment baking); the article FlowDocument is V2 with kind:'article'.
    const projectAnalytics = await this.analytics.getSettings(projectId);
    const publishResult = await this.builderClient.publishWeb({
      alias,
      funnelId: articleId,
      versionId: ARTICLE_DRAFT_VERSION_ID,
      config: flowDocument,
      projectId,
      // placement.config is optional on WebPlacement; created placements always
      // carry funnelId/versionId (see create above). Assert to keep the exact
      // runtime payload unchanged for legacy placements without config.
      webPlacementConfig: {
        ...placement.config,
        id: placement.id,
        entityName: placement.name,
      } as WebPlacementConfig,
      projectAnalytics,
    });

    if (!publishResult.success) {
      return {
        success: false,
        webUrl: publishResult.webUrl || '',
        placementId: placement.id,
        error: 'Builder Service reported failure',
      };
    }

    // 3. Mark the placement published (status + URL bookkeeping).
    await this.placements.markPublished(
      projectId,
      placement.id,
      createdBy,
      publishResult.webUrl,
      publishResult.webShellVersion,
    );

    return {
      success: true,
      webUrl: this.absolutizeWebUrl(publishResult.webUrl, projectId),
      placementId: placement.id,
    };
  }

  /**
   * The builder returns a RELATIVE webUrl (e.g. "/my-alias") in local mode —
   * resolve it against the Edge Gateway origin so window.open never lands on
   * the Studio frontend origin. projectId query keeps default-domain serving
   * project-scoped.
   */
  private absolutizeWebUrl(webUrl: string, projectId: string): string {
    if (!webUrl.startsWith('/')) return webUrl;
    const gatewayOrigin = import.meta.env.VITE_EDGE_GATEWAY_URL as string | undefined;
    if (!gatewayOrigin) return webUrl;
    return `${gatewayOrigin.replace(/\/$/, '')}${webUrl}?projectId=${encodeURIComponent(projectId)}`;
  }
}
